"use client";

import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styles from "./VideoModal.module.scss";
import VideoPlayer from "@components/molecules/VideoPlayer/VideoPlayer";
import {
  VideoModalProps,
  DeviceType,
  VideoPlayerConfig,
  VideoPlayerEvents,
} from "@interfaces/video";

const VideoModal: React.FC<VideoModalProps> = ({
  video,
  metadata,
  isOpen,
  onClose,
  deviceType,
  config = {},
  events = {},
  hasDesktopVideo = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Default config for modal - force autoplay for better UX
  const defaultConfig: VideoPlayerConfig = {
    autoplay: true,
    muted: true, // Mute to allow autoplay (browsers require muted autoplay)
    controls: true,
    showTitle: true,
    showProgress: true,
    showVolume: true,
    showClose: true,
    ...config,
  };

  // Combined events with close handler
  const modalEvents: VideoPlayerEvents = {
    ...events,
    onClose,
  };

  // Handle escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
      return () => {
        document.removeEventListener("keydown", handleEscapeKey);
      };
    }
  }, [isOpen, onClose]);

  // Handle focus management
  useEffect(() => {
    if (isOpen) {
      // Store the currently focused element
      previousFocusRef.current = document.activeElement as HTMLElement;

      // Prevent body scroll
      document.body.style.overflow = "hidden";

      // Focus the modal
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.focus();
        }
      }, 100);
    } else {
      // Restore body scroll
      document.body.style.overflow = "";

      // Restore focus to the previously focused element
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    }

    return () => {
      // Cleanup on unmount
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Handle click outside to close (desktop only)
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (
      event.target === event.currentTarget &&
      deviceType === DeviceType.DESKTOP
    ) {
      onClose();
    }
  };

  // Handle focus trap within modal
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Tab") {
      const focusableElements = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements && focusableElements.length > 0) {
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[
          focusableElements.length - 1
        ] as HTMLElement;

        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    }
  };

  if (!isOpen || !video || !metadata) {
    return null;
  }

  const modalContent = (
    <div
      ref={modalRef}
      className={`${styles.videoModal} ${styles[`videoModal--${deviceType}`]}`}
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="video-modal-title"
      aria-describedby="video-modal-description"
      tabIndex={-1}
    >
      {/* Screen reader announcements */}
      <div id="video-modal-title" className={styles.srOnly}>
        {metadata.title}
      </div>
      <div id="video-modal-description" className={styles.srOnly}>
        Video player modal. Press Escape to close.
      </div>

      <div className={styles.videoModalContent}>
        <VideoPlayer
          video={video}
          metadata={metadata}
          config={defaultConfig}
          events={modalEvents}
          deviceType={deviceType}
          isVisible={isOpen}
          className={`${styles.modalVideoPlayer} ${hasDesktopVideo ? styles.modalVideoPlayerDesktop : ''}`}
        />
      </div>
    </div>
  );

  // Render to portal for proper z-index management
  if (typeof window !== "undefined") {
    return createPortal(modalContent, document.body);
  }

  return null;
};

export default VideoModal;
